import axios from "axios";
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  tableCellClasses,
  Box,
} from "@mui/material";
import { styled } from "@mui/styles";
import localize from "../../../../common/localize";
import ApplicationLogoVertical from "../../../../components/common/ApplicationLogoVertical";
import { useEffect, useState } from "react";
import useGlobalContext from "../../../../hooks/useGlobalContext";
import { downloadBlob } from "../../../../common/EntitiesManagement";
import { applyReportTitleColors } from "../../../../common/utils";
import LoadingPanel from "../../../../components/common/LoadingPanel";

export default function ReportHeader({
  event,
  isOriginal,
  customData,
  showContractorData,
}) {
  const [companyData, setCompanyData] = useState(undefined);
  const { CONTRACTOR_API, COMPANY_ENDPOINT, config } = useGlobalContext();
  const [logoBlob, setLogoBlob] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [contractorData, setContractorData] = useState(undefined);

  useEffect(() => {
    if (event) {
      if (event?.Contractor) {
        setContractorData(
          event?.Contractor
            ? {
                ...event.Contractor,
                reportSetup: JSON.parse(event.Contractor.reportSetup ?? "{}"),
              }
            : undefined,
        );
      } else {
        getCompanyData();
      }
    } else {
      getCompanyData();
    }
  }, [event]);

  useEffect(() => {
    if (contractorData) {
      getCompanyData();
    }
  }, [contractorData]);

  useEffect(() => {
    if (companyData) {
      applyReportTitleColors(
        companyData?.ReportBackgroundColorHex,
        companyData?.ReportForegroundColorHex,
      );
    }
  }, [companyData]);

  const getEventCode = () =>
    event?.Code && event?.Code !== ""
      ? event?.Code ?? localize.no_data
      : `${localize.app_name}-${event?.Id ?? localize.no_data}`;

  const getLabelValue = (value, classNameValue) => (
    <div
      style={{
        display: "flex",
        alignContent: "center",
        justifyContent: "center",
      }}
    >
      <label className={classNameValue}>{value}</label>
    </div>
  );

  const StyledTableCell = styled(TableCell)({
    padding: "5px",
  });

  const getLogoBlob = (companyId, companyHasLogo) => {
    const contractorHasLogo =
      showContractorData &&
      contractorData?.reportSetup?.enabled &&
      contractorData?.reportSetup?.data?.logo &&
      contractorData?.companyLogo;

    if (contractorHasLogo) {
      downloadBlob(
        `${CONTRACTOR_API}/${contractorData?.Id}/logo/download`,
        config,
        setLogoBlob,
      );
      return;
    }

    if (companyHasLogo && companyId > 0) {
      downloadBlob(
        `${COMPANY_ENDPOINT}/${companyId}/logo/download`,
        config,
        setLogoBlob,
      );
      return;
    }

    setLogoBlob(undefined);
  };

  const getCompanyData = async () => {
    setIsLoading(true);
    try {
      const companyRequestResult = await axios.get(
        `${COMPANY_ENDPOINT}`,
        config,
      );

      const companyInfo = companyRequestResult?.data?.Data;
      if (companyRequestResult?.data?.Ok && companyInfo) {
        setCompanyData({
          ...companyInfo,
          FileName: companyInfo?.LogoPath?.split("/")?.pop(),
        });
      }

      getLogoBlob(
        companyInfo?.Id,
        !!companyInfo?.LogoPath &&
          companyInfo.LogoPath.replace(" ", "") !== "",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getHeaderData = () => {
    return (
      <Box className="w-full flex-col">
        {getLabelValue(
          event?.EventType?.reportTitle
            ? event?.EventType?.reportTitle
            : localize.subdirectorate_environmental_management,
          "report_normal_text report_centered_label report_bold_text",
        )}
        {getLabelValue(
          getEventCode(),
          "report_normal_text report_centered_label report_bold_text",
        )}

        {event?.EventType?.operationalArea?.Name &&
          getLabelValue(
            `${localize.area}: ${event?.EventType?.operationalArea?.Name}`,
            "report_normal_text report_centered_label report_bold_text",
          )}

        {event?.ProceedingInfo?.Name &&
          getLabelValue(
            `${localize.proceeding}: ${event?.ProceedingInfo?.Name}`,
            "report_normal_text report_centered_label report_bold_text",
          )}
      </Box>
    );
  };

  const getCompanyOrContractorData = () => {
    if (showContractorData && contractorData?.reportSetup?.enabled) {
      return (
        <div className="w-full flex-col items-center justify-center">
          {contractorData?.reportSetup?.data?.name &&
            contractorData?.Name &&
            getLabelValue(
              contractorData?.Name,
              "report_little_text report_centered_label report_bold_text",
            )}
          {contractorData?.reportSetup?.data?.identification &&
            contractorData?.Identification &&
            getLabelValue(
              contractorData?.Identification,
              "report_little_text report_centered_label report_bold_text",
            )}
          {contractorData?.reportSetup?.data?.address &&
            contractorData?.Address &&
            getLabelValue(
              contractorData?.Address,
              "report_little_text report_centered_label report_bold_text",
            )}
          {contractorData?.reportSetup?.data?.phoneNumber &&
            contractorData?.PhoneNumber &&
            getLabelValue(
              contractorData?.PhoneNumber,
              "report_little_text report_centered_label report_bold_text",
            )}
          {contractorData?.reportSetup?.data?.email &&
            contractorData?.Email &&
            getLabelValue(
              contractorData?.Email,
              "report_little_text report_centered_label report_bold_text",
            )}
          {isOriginal &&
            getLabelValue(
              `(${localize.generate_original_report})`,
              "report_little_text report_disabled_text report_margin_top_10 report_centered_label report_bold_text",
            )}
        </div>
      );
    }

    return (
      <div className="w-full flex-col items-center justify-center">
        {companyData?.Name &&
          getLabelValue(
            companyData.Name,
            "report_little_text report_centered_label report_bold_text",
          )}
        {companyData?.Identification &&
          getLabelValue(
            companyData.Identification,
            "report_little_text report_centered_label report_bold_text",
          )}
        {companyData?.PhoneNumber &&
          getLabelValue(
            companyData.PhoneNumber,
            "report_little_text report_centered_label report_bold_text",
          )}
        {isOriginal &&
          getLabelValue(
            `(${localize.generate_original_report})`,
            "report_little_text report_disabled_text report_margin_top_10 report_centered_label report_bold_text",
          )}
      </div>
    );
  };

  return (
    <div
      className="w-full mt-2"
      style={{ border: "1px solid black", borderRadius: "5px" }}
    >
      {isLoading ? (
        <LoadingPanel donotUseHscreen />
      ) : (
        <TableContainer>
          <Table
            sx={{
              maxWidth: "100%",
              [`& .${tableCellClasses.root}`]: {
                borderBottom: "none",
              },
            }}
            aria-label="simple table"
          >
            <TableBody>
              <TableRow
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <StyledTableCell scope="row" width={120} key="header_logo">
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minWidth: 100,
                    }}
                  >
                    <ApplicationLogoVertical
                      height={100}
                      width={100}
                      path={
                        logoBlob ??
                        `${process.env.PUBLIC_URL}/img/app_report_header.png`
                      }
                    />
                  </div>
                </StyledTableCell>

                <TableCell width="60%" key="header_data">
                  {customData && customData.length > 0 ? (
                    <div className="w-full flex-col">
                      {customData.map((x) =>
                        getLabelValue(
                          x,
                          "report_normal_text report_centered_label report_bold_text",
                        ),
                      )}
                    </div>
                  ) : (
                    getHeaderData()
                  )}
                </TableCell>

                <TableCell width={200} key="header_company_info">
                  {companyData && getCompanyOrContractorData()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
